import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogPage = () => {
  const { language, t } = useLanguage();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchArticles = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/blog`);
      setArticles(response.data);
    } catch (err) {
      setArticles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchArticles(); }, [fetchArticles]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'pt' ? 'pt-BR' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const renderContent = () => {
    if (loading) return <div className="text-center py-12"><p className="text-steel">{t('Carregando...', 'Loading...')}</p></div>;
    if (articles.length === 0) return <div className="text-center py-12"><p className="text-steel">{t('Nenhum artigo publicado ainda. Em breve teremos conteúdo para você!', 'No articles published yet. Content coming soon!')}</p></div>;
    // Grid centralizado quando há apenas 1-2 artigos
    const isSmall = articles.length <= 2;
    return (
      <div
        className={
          isSmall
            ? 'grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl mx-auto'
            : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6'
        }
        data-testid="blog-grid"
      >
        {articles.map((article) => (
          <motion.article key={article.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-[#E6E6ED] rounded-md overflow-hidden hover:border-cobalt transition-colors duration-300"
            data-testid={`blog-article-${article.id}`}>
            <img src={article.image_url} alt={language === 'pt' ? article.title_pt : article.title_en} className="w-full h-48 object-cover" loading="lazy" />
            <div className="p-6">
              <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-cobalt">{article.category}</span>
              <h2 className="text-lg font-bold text-abyssal mt-2 mb-3 leading-snug">{language === 'pt' ? article.title_pt : article.title_en}</h2>
              <p className="text-[15px] text-steel mb-4 line-clamp-3 leading-relaxed">{language === 'pt' ? article.excerpt_pt : article.excerpt_en}</p>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2 text-xs text-steel-400">
                  <Calendar className="h-3.5 w-3.5" />
                  <span>{formatDate(article.created_at)}</span>
                </div>
                <Link to={`/blog/${article.slug}`} data-testid={`blog-link-${article.id}`}
                  className="text-cobalt hover:text-abyssal font-semibold text-sm flex items-center gap-1 underline underline-offset-4 decoration-cobalt/40 hover:decoration-abyssal">
                  <span>{t('Ler mais', 'Read more')}</span>
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>
          </motion.article>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen pt-24 bg-white">
      <section className="py-16 md:py-20 bg-white border-b border-[#E6E6ED]">
        <div className="cv-container">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-brass"></span>
              <span className="text-[11px] font-semibold uppercase tracking-[0.28em] text-brass">BLOG</span>
            </div>
            <h1 className="text-[40px] sm:text-5xl lg:text-[60px] tracking-tight font-bold text-abyssal leading-[1.05] mb-6">
              {t('Conteúdos sobre ', 'Insights on ')}
              <span className="text-cobalt">{t('comércio exterior.', 'international trade.')}</span>
            </h1>
            <p className="text-lg text-steel leading-relaxed">
              {t(
                'Acompanhe artigos, análises e atualizações sobre importação, logística e mercado internacional.',
                'Stay updated with articles, insights, and updates about import operations, logistics, and global trade.'
              )}
            </p>
          </motion.div>
        </div>
      </section>
      <section className="py-16 md:py-20 bg-mist">
        <div className="cv-container">{renderContent()}</div>
      </section>
      {/* CTA compacto antes do footer */}
      <section className="bg-white border-t border-[#E6E6ED] py-14 md:py-16">
        <div className="cv-container">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 max-w-4xl mx-auto">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.28em] text-abyssal mb-2">
                {t('FALE COM A COSTA VALENTE', 'CONTACT COSTA VALENTE')}
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-abyssal leading-tight">
                {t('Precisa de apoio na sua próxima importação?', 'Need support on your next import?')}
              </h2>
            </div>
            <Link to="/contato" data-testid="blog-cta-compact"
              className="inline-flex items-center gap-2 bg-abyssal hover:bg-cobalt text-white px-6 py-3 rounded-md font-semibold text-sm transition-colors self-start md:self-auto">
              {t('Falar com nossa equipe', 'Talk to our team')}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
